import { Link } from "react-router-dom";
import { ArrowRight, FileText, Mic, Globe, Sparkles, Zap, Clock } from "lucide-react";
import Navbar from "@/components/Navbar";
import ReviewsList from "@/components/ReviewsList";
import StructuredData from "@/components/StructuredData";

const heroImg = "https://images.unsplash.com/photo-1615914143778-1a1a6e50c5dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB0YWtpbmclMjBub3RlcyUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzgzOTM4MzAyfDA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: FileText, title: "Any transcript or PDF", body: "Paste raw text or drop a PDF. We rip out the fluff and keep the signal." },
  { icon: Mic, title: "Audio & video lectures", body: "Upload MP3, WAV, MP4 or WebM. Whisper transcribes, then we structure." },
  { icon: Globe, title: "Website URLs", body: "Point us at any article, blog or docs page. We fetch and condense." },
  { icon: Zap, title: "Structured output", body: "Core concepts, flowcharts, formulas, exam questions — always in one place." },
  { icon: Clock, title: "5-minute revision", body: "Every note ships with a one-minute recap for exam night." },
  { icon: Sparkles, title: "Meeting mode", body: "Toggle to meetings and get decisions, owners, and deadlines instead." },
];

const MARQUEE = ["Lectures", "Meetings", "Podcasts", "PDFs", "Articles", "Standups", "Interviews", "Webinars"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F0F9FF] relative noise">
      <Navbar />
      <StructuredData />

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 pt-16 sm:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <span data-testid="hero-eyebrow" className="inline-block font-mono text-xs uppercase tracking-[0.2em] bg-black text-[#3B82F6] px-3 py-1">
              Smart S Notes · v1
            </span>
            <span data-testid="hero-discount" className="ml-2 inline-block font-mono text-xs uppercase tracking-[0.2em] bg-[#FF3333] text-white px-3 py-1 border-2 border-black">
              Launch · 53% OFF
            </span>
            <h1
              data-testid="hero-title"
              className="mt-6 font-heading font-black text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tighter"
            >
              Long videos.<br />
              One easy page.<br />
              <span className="bg-[#BFDBFE] px-2">Under a minute.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg sm:text-xl text-zinc-700 leading-relaxed">
              Drop a long lecture, meeting recording, YouTube link, PDF, or your handwritten
              photo. Get one easy-to-grab page — with definitions, formulas, diagrams, sticky
              reminders, MCQs, and flashcards — in under a minute.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                data-testid="hero-cta-primary"
                to="/register"
                className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest px-6 py-4 bg-[#3B82F6] text-white btn-brutal"
              >
                Make my 1-page notes <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                data-testid="hero-cta-secondary"
                to="/login"
                className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest px-6 py-4 border-2 border-black hover:bg-black hover:text-white transition-colors"
              >
                I have an account
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="relative border-4 border-black overflow-hidden">
              <img src={heroImg} alt="Student taking notes" className="w-full h-72 lg:h-96 object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-black text-white px-4 py-3 font-mono text-xs uppercase tracking-widest flex justify-between">
                <span>2h Lecture</span>
                <span className="text-[#3B82F6]">→ 1 page · &lt; 60s</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y-2 border-black bg-black text-[#3B82F6] overflow-hidden">
        <div className="marquee-inner whitespace-nowrap py-4 flex gap-10 w-max font-heading font-black text-3xl uppercase tracking-tight">
          {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((w, i) => (
            <span key={i} className="flex items-center gap-10">
              {w} <span className="text-white">✕</span>
            </span>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-20 sm:py-28">
        <div className="grid lg:grid-cols-3 gap-8 items-end mb-12">
          <div className="lg:col-span-2">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">/ Features</p>
            <h2 className="mt-2 font-heading font-black text-4xl sm:text-5xl tracking-tight">
              Not another summarizer.<br />
              A revision-grade note engine.
            </h2>
          </div>
          <p className="text-zinc-600">
            We keep only the highest-value signal: definitions, laws, dates, formulas, and the
            exact points your speaker repeated three times.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
          {FEATURES.map(({ icon: Icon, title, body }, i) => (
            <div key={i} className="bg-white p-8 hover:bg-[#3B82F6]/20 transition-colors">
              <div className="w-10 h-10 border-2 border-black flex items-center justify-center mb-6">
                <Icon className="w-4 h-4" />
              </div>
              <h3 className="font-heading font-bold text-xl">{title}</h3>
              <p className="mt-2 text-zinc-600">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Big CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="bg-black text-white p-10 sm:p-16 border-4 border-black relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-64 h-64 bg-[#3B82F6] rotate-12" />
          <div className="relative">
            <h2 className="font-heading font-black text-4xl sm:text-6xl tracking-tight max-w-2xl">
              Long videos.<br />One easy page. Under a minute.
            </h2>
            <Link
              data-testid="footer-cta"
              to="/register"
              className="inline-flex items-center gap-2 mt-10 font-mono text-sm uppercase tracking-widest px-6 py-4 bg-[#3B82F6] text-white btn-brutal"
            >
              Turn my long video into a page <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <ReviewsList scope="app" limit={6} heading="What students say" />

      <footer className="border-t border-black/10 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-between gap-4 font-mono text-xs uppercase tracking-widest text-zinc-500">
          <div>© 2026 Smart S Notes</div>
          <div>Built for students & operators</div>
        </div>
      </footer>
    </div>
  );
}
